import { useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import * as cognito from './cognito';
import { useAuth } from './AuthContext';
import { adminCheck } from './api';
import { ensureThemeApplied } from './theme';
import HomePage from './pages/HomePage';
import ProfilePage from './pages/ProfilePage';
import AdminCategoriesPage, { AdminCategoriesContent } from './pages/AdminCategoriesPage';
import AnalysisPage from './pages/AnalysisPage';
import RecurringPage from './pages/RecurringPage';

type LoginStep = 'credentials' | 'mfa' | 'new_password';

function LoginPage() {
  const { session, setSession } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<LoginStep>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [cognitoSession, setCognitoSession] = useState('');
  const [mfaKind, setMfaKind] = useState<'software_token_mfa' | 'sms_mfa'>('software_token_mfa');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (session) navigate('/', { replace: true });
  }, [session, navigate]);

  function finishLogin(s: cognito.AuthSession) {
    cognito.saveSession(s);
    setSession(s);
    navigate('/', { replace: true });
  }

  async function onCredentials(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const r = await cognito.loginWithPassword(email, password);
      if (r.status === 'authenticated') {
        finishLogin(r.session);
        return;
      }
      if (r.status === 'software_token_mfa' || r.status === 'sms_mfa') {
        setCognitoSession(r.cognitoSession);
        setMfaKind(r.status);
        setStep('mfa');
        return;
      }
      if (r.status === 'new_password_required') {
        setCognitoSession(r.cognitoSession);
        setStep('new_password');
        return;
      }
    } catch (x) {
      setErr(x instanceof Error ? x.message : 'Errore');
    } finally {
      setBusy(false);
    }
  }

  async function onMfa(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const s =
        mfaKind === 'sms_mfa'
          ? await cognito.completeSmsMfa(email, cognitoSession, mfaCode)
          : await cognito.completeSoftwareTokenMfa(email, cognitoSession, mfaCode);
      finishLogin(s);
    } catch (x) {
      setErr(x instanceof Error ? x.message : 'Errore');
    } finally {
      setBusy(false);
    }
  }

  async function onNewPassword(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    if (newPassword !== newPassword2) {
      setErr('Le password non coincidono');
      return;
    }
    if (newPassword.length < 8) {
      setErr('La password deve avere almeno 8 caratteri');
      return;
    }
    setBusy(true);
    try {
      const r = await cognito.completeNewPassword(email, cognitoSession, newPassword);
      if (r.status === 'authenticated') {
        finishLogin(r.session);
        return;
      }
      if (r.status === 'software_token_mfa' || r.status === 'sms_mfa') {
        setCognitoSession(r.cognitoSession);
        setMfaKind(r.status);
        setStep('mfa');
        setMfaCode('');
        return;
      }
      setErr('Passaggio non completato. Riprova ad accedere.');
    } catch (x) {
      setErr(x instanceof Error ? x.message : 'Errore');
    } finally {
      setBusy(false);
    }
  }

  function backToCredentials() {
    setStep('credentials');
    setErr('');
    setMfaCode('');
    setNewPassword('');
    setNewPassword2('');
    setCognitoSession('');
  }

  return (
    <div className="auth-layout">
      <h1 className="auth-title">Spese Famiglia</h1>

      {step === 'credentials' && (
        <form className="auth-card" onSubmit={onCredentials}>
          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {err && <p className="field-err">{err}</p>}
          <button className="btn-primary" type="submit" disabled={busy}>
            {busy ? '…' : 'Accedi'}
          </button>
        </form>
      )}

      {step === 'mfa' && (
        <form className="auth-card" onSubmit={onMfa}>
          <p className="auth-hint">
            {mfaKind === 'sms_mfa'
              ? 'Inserisci il codice ricevuto via SMS.'
              : 'Inserisci il codice a 6 cifre dall’app authenticator (MFA).'}
          </p>
          <div className="field">
            <label htmlFor="mfa">Codice</label>
            <input
              id="mfa"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              required
            />
          </div>
          {err && <p className="field-err">{err}</p>}
          <button className="btn-primary" type="submit" disabled={busy}>
            {busy ? '…' : 'Conferma'}
          </button>
          <button type="button" className="btn-secondary" onClick={backToCredentials}>
            Indietro
          </button>
        </form>
      )}

      {step === 'new_password' && (
        <form className="auth-card" onSubmit={onNewPassword}>
          <p className="auth-hint">Imposta una nuova password per il tuo account.</p>
          <div className="field">
            <label htmlFor="np1">Nuova password</label>
            <input
              id="np1"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <div className="field">
            <label htmlFor="np2">Ripeti password</label>
            <input
              id="np2"
              type="password"
              autoComplete="new-password"
              value={newPassword2}
              onChange={(e) => setNewPassword2(e.target.value)}
              required
              minLength={8}
            />
          </div>
          {err && <p className="field-err">{err}</p>}
          <button className="btn-primary" type="submit" disabled={busy}>
            {busy ? '…' : 'Salva e accedi'}
          </button>
          <button type="button" className="btn-secondary" onClick={backToCredentials}>
            Indietro
          </button>
        </form>
      )}
    </div>
  );
}

function HomeOrAdmin() {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    adminCheck()
      .then((r) => setIsAdmin(r.admin))
      .catch(() => setIsAdmin(false));
  }, []);

  if (isAdmin === null) {
    return <p className="home-muted">Caricamento…</p>;
  }
  return isAdmin ? <AdminCategoriesContent /> : <HomePage />;
}

function AppShell() {
  const { displayName, avatarUrl } = useAuth();

  useEffect(() => {
    ensureThemeApplied();
  }, []);

  return (
    <div className="app-shell">
      <header className="app-header">
        <Link to="/" className="app-brand app-brand-link">
          Spese
        </Link>
        <NavLink
          to="/analisi"
          className={({ isActive }) => `app-nav-link${isActive ? ' app-nav-link--active' : ''}`}
        >
          Analisi
        </NavLink>
        <Link to="/profilo" className="header-avatar-link" aria-label="Profilo e impostazioni">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="header-avatar-img" />
          ) : (
            <span className="header-avatar-fallback">{displayName.charAt(0).toUpperCase() || '?'}</span>
          )}
        </Link>
      </header>
      <main className="app-main">
        <Outlet />
      </main>
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { session } = useAuth();
  if (!session) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<HomeOrAdmin />} />
        <Route path="analisi" element={<AnalysisPage />} />
        <Route path="ricorrenti" element={<RecurringPage />} />
        <Route path="profilo" element={<ProfilePage />} />
        <Route path="categorie" element={<AdminCategoriesPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
